import React, { useState } from 'react';
import { Box, Button, FileField, Form, HStack, SubmitButton, Text } from '@medely/ui-kit';
import { useWatch } from '@medely/ui-kit/react-hook-form';
import { validateResumeFile } from '@mp/utils/fileValidation';
import { useResumeModal } from './useResumeModal';
import { parseFileString } from './utils';

const buildFormData = (file: unknown) => {
  const formData = new FormData();
  formData.append('professional[resume]', file as File);
  return formData;
};

type FormContentProps = {
  onClose: () => void;
  isLoading: boolean;
  fileErrorMessage: string | null;
  handleFileChange: (value: unknown) => unknown;
  initialResume: unknown;
};

const FormContent = ({
  onClose,
  isLoading,
  fileErrorMessage,
  handleFileChange,
  initialResume,
}: FormContentProps) => {
  const currentResume = useWatch({ name: 'resume' });
  const hasResumeChanged = currentResume !== initialResume;

  return (
    <>
      <Box my={2}>
        <FileField
          acceptedFileTypes={['.doc', '.docx', '.pdf']}
          name="resume"
          label="Upload your resume"
          formatOnChange={(newValue) => handleFileChange(newValue)}
          parseValue={(value) => (typeof value === 'string' ? parseFileString(value) : value)}
        />
        {fileErrorMessage && (
          <Box mt={1}>
            <Text size="s" color="state.error.secondary">
              {fileErrorMessage}
            </Text>
          </Box>
        )}
      </Box>
      <HStack>
        <Box mr={0.5} width="50%">
          <Button variant="outlined" onClick={onClose} fullWidth>
            Back
          </Button>
        </Box>
        <Box ml={0.5} width="50%">
          <SubmitButton fullWidth disabled={isLoading || !!fileErrorMessage || !hasResumeChanged}>
            Save
          </SubmitButton>
        </Box>
      </HStack>
    </>
  );
};

export const ExperienceSummaryResumeInfo = ({ onClose }: { onClose: () => void }) => {
  const { resume, schema, handleContinue, isLoading } = useResumeModal({
    onClose,
    buildFormData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const [fileErrorMessage, setFileErrorMessage] = useState<string | null>(null);

  const handleFileChange = (value: unknown) => {
    if (value instanceof File) {
      const validation = validateResumeFile(value);
      setFileErrorMessage(validation.isValid ? null : validation.error || 'Invalid file');
    } else {
      setFileErrorMessage(null);
    }
    return value;
  };

  return (
    <Form defaultValues={{ resume }} handleSubmit={handleContinue} mode="onChange" schema={schema}>
      <FormContent
        onClose={onClose}
        isLoading={isLoading}
        fileErrorMessage={fileErrorMessage}
        handleFileChange={handleFileChange}
        initialResume={resume}
      />
    </Form>
  );
};
